import { useState } from "react";
import { ChevronDownIcon, SparklesIcon, TriangleAlertIcon } from "lucide-react";

import { CalenderSparkle } from "@/assets/CalenderSparkle";
import { Column, Row, Text } from "@/components";
import { ShortlistTakeoverDialog } from "@jobs/pipeline/shared/ShortlistTakeoverDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  PausedJobMoveDecision,
  usePausedJobMoveGate,
} from "@/contexts/PausedJobMoveGateContext";

import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetInterviews } from "@/hooks/queries/useGetInterviews";
import { useGetJobInternalConfig } from "@/hooks/queries/useGetJobInternalConfig";
import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { useInvalidateNotifications } from "@/hooks/invalidate/useInvalidateNotifications";
import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import {
  candidacyStatusOptions,
  candidacyStatusToBackgroundColor,
  candidacyStatusToLabel,
  defaultInterviewQueryParams,
  getSourcedType,
  NON_TERMINAL_INTERVIEW_STATUSES,
  sourcedTypeToDotColor,
  sourcedTypeToLabel,
} from "@/lib/constants";
import { screeningAvailability } from "@/lib/screeningAvailability";
import { cn } from "@/lib/utils";
import {
  CandidacyInfoResponse,
  CandidacyStatus,
  InterviewType,
  ShortlistTakeover,
} from "@/types";

const statusChangeDialogStatuses = [
  CandidacyStatus.INTERVIEW,
  CandidacyStatus.SCREENSCHEDULED,
  CandidacyStatus.SCREENED,
  CandidacyStatus.OFFER,
  CandidacyStatus.PLACED,
  CandidacyStatus.EMPLOYED,
  CandidacyStatus.EMPLOYMENT_ENDED,
];

export const CandidacyStatusDropdown = ({
  candidacyInfo,
  onStatusChanged,
  setOpenStatusChangeDialog,
}: {
  candidacyInfo: CandidacyInfoResponse;
  onStatusChanged?: (newStatus: CandidacyStatus) => void;
  setOpenStatusChangeDialog: (status: CandidacyStatus) => void;
}) => {
  /* Database CRUD Operations */
  // Read
  const currentUser = useGetCurrentUser();
  const { data: jobInternalConfig } = useGetJobInternalConfig();
  const { data: userSettings } = useGetUserSettings();
  const { data: existingInterviews } = useGetInterviews({
    jobId: candidacyInfo.jobId,
    queryOptions: {
      ...defaultInterviewQueryParams,
      candidacyId: candidacyInfo.id,
    },
  });

  // Update
  const { mutate: updateCandidacy, isPending } = useUpdateCandidacy();
  const pausedJobMoveGate = usePausedJobMoveGate();

  /* Cache Updates */
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();
  const invalidateNotifications = useInvalidateNotifications();

  /* React Hooks */
  const [open, setOpen] = useState(false);
  const [takeover, setTakeover] = useState<ShortlistTakeover | null>(null);

  /* Helpers */
  // Applied candidacies that were sourced or resurrected show that badge in
  // place of the "Applied" status.
  const sourcedType =
    candidacyInfo.status === CandidacyStatus.APPLIED
      ? getSourcedType(
          candidacyInfo.source,
          candidacyInfo.isExternallySourced,
          candidacyInfo.isAutoResurrected,
          // Back from the Stevie call a staffer sent them to.
          candidacyInfo.stevieNudgedAt !== null &&
            candidacyInfo.stevieCallEndedAt !== null,
        )
      : null;
  const statusLabel = sourcedType
    ? sourcedTypeToLabel[sourcedType]
    : candidacyStatusToLabel[candidacyInfo.status];
  const statusDotColor = sourcedType
    ? sourcedTypeToDotColor[sourcedType]
    : candidacyStatusToBackgroundColor[candidacyInfo.status];
  const isSchedulingAgentActive = !!jobInternalConfig?.stafferAgentActive;

  const shortlistBlocked = screeningAvailability.blocksShortlist({
    candidacyStatus: candidacyInfo.status,
    jobAgentActive: isSchedulingAgentActive,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    availability: userSettings?.screeningAvailability || null,
  });

  const hasInternalScreenScheduled =
    existingInterviews?.some(
      (interview) =>
        interview.type === InterviewType.INTERNAL &&
        interview.status !== null &&
        NON_TERMINAL_INTERVIEW_STATUSES.includes(interview.status),
    ) ?? false;

  const filteredCandidacyStatusOptions = candidacyStatusOptions.filter(
    (candidacyStatusOption) =>
      candidacyStatusOption.value !== CandidacyStatus.REJECTED &&
      candidacyStatusOption.value !== CandidacyStatus.CLOSED,
  );

  const isDisabled = (optionValue: CandidacyStatus) => {
    if (optionValue === CandidacyStatus.SHORTLIST && shortlistBlocked)
      return true;
    if (candidacyInfo.status === CandidacyStatus.OFFER)
      return (
        optionValue !== CandidacyStatus.PLACED &&
        optionValue !== CandidacyStatus.INTERVIEW
      );
    else if (candidacyInfo.status === CandidacyStatus.PLACED)
      return (
        optionValue !== CandidacyStatus.EMPLOYED &&
        optionValue !== CandidacyStatus.OFFER &&
        optionValue !== CandidacyStatus.INTERVIEW
      );
    else if (candidacyInfo.status === CandidacyStatus.EMPLOYED)
      return optionValue !== CandidacyStatus.EMPLOYMENT_ENDED;
    else if (
      optionValue === CandidacyStatus.EMPLOYED ||
      optionValue === CandidacyStatus.EMPLOYMENT_ENDED
    )
      return true;
    return optionValue === candidacyInfo.status || isPending;
  };

  /* Handlers */
  const applyStatusChange = (newStatus: CandidacyStatus) => {
    // Fired optimistically so dependent UI (e.g. the enrich dialog) opens
    // instantly rather than after the request round-trip.
    onStatusChanged?.(newStatus);

    updateCandidacy(
      {
        candidacyId: candidacyInfo.id,
        updateCandidacyRequest: {
          fromStatus: candidacyInfo.status,
          status: newStatus,
        },
      },
      {
        onSuccess: (data) => {
          setOpen(false);

          setCandidacyInAllLists({
            candidacyId: candidacyInfo.id,
            lastCandidacyStatusChange: {
              createdAt: new Date().toISOString(),
              fromStatus: candidacyInfo.status,
              toStatus: newStatus,
              statusChangeReason: null,
              byAgent: false,
            },
          });
          setCandidacyInfo({
            candidacyId: candidacyInfo.id,
            status: newStatus,
            lastStatusChangeByAgent: false,
            candidacyOwners: data.candidacyOwners,
          });

          // A status change out of APPLIED clears the "excellent candidate"
          // notification on the backend; refetch so the bell badge drops now.
          invalidateNotifications();

          // https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2187447622
          setTimeout(() => {
            const body = document.querySelector("body");
            if (body) {
              body.style.pointerEvents = "";
            }
          }, 100);
        },
      },
    );
  };

  const handleStatusChange = async (newStatus: CandidacyStatus) => {
    if (newStatus === candidacyInfo.status) {
      setOpen(false);
      return;
    }

    const decision = await pausedJobMoveGate?.requestDecision({
      candidacyId: candidacyInfo.id,
      fromStatus: candidacyInfo.status,
      jobId: candidacyInfo.jobId,
      toStatus: newStatus,
    });
    if (decision === PausedJobMoveDecision.CANCEL) {
      setOpen(false);
      return;
    }

    const isScreenTransition = [
      CandidacyStatus.SCREENED,
      CandidacyStatus.SCREENSCHEDULED,
    ].includes(newStatus);
    if (
      statusChangeDialogStatuses.includes(newStatus) &&
      !(isScreenTransition && hasInternalScreenScheduled)
    ) {
      setOpenStatusChangeDialog(newStatus);
      return;
    }

    // Shortlisting another staffer's candidacy prompts a takeover choice first.
    if (newStatus === CandidacyStatus.SHORTLIST) {
      const ownerUserId =
        candidacyInfo.candidacyOwners.length > 0
          ? candidacyInfo.candidacyOwners[0].userId
          : null;
      if (ownerUserId !== null && ownerUserId !== currentUser?.id) {
        setOpen(false);
        setTakeover({
          candidacyId: candidacyInfo.id,
          ownerUserId,
          onShortlist: () => {
            applyStatusChange(newStatus);
          },
        });
        return;
      }
    }

    applyStatusChange(newStatus);
  };

  /* Render */
  const renderStatusOption = (candidacyStatusOption: {
    label: string;
    value: CandidacyStatus;
  }) => {
    const isShortlistOption =
      candidacyStatusOption.value === CandidacyStatus.SHORTLIST;
    const isShortlistGate = isShortlistOption && shortlistBlocked;

    return (
      <DropdownMenuItem
        className="flex items-start gap-2"
        disabled={isDisabled(candidacyStatusOption.value)}
        key={candidacyStatusOption.value}
        onClick={() => {
          void handleStatusChange(candidacyStatusOption.value);
        }}
      >
        <div
          className={cn(
            "mt-1 h-3 w-3 shrink-0 rounded-full",
            candidacyStatusToBackgroundColor[candidacyStatusOption.value],
          )}
        />
        <Column className="gap-0.5">
          <Row className="items-center gap-1.5">
            <Text className="text-sm font-medium text-slate-700">
              {candidacyStatusOption.label}
            </Text>
            {isShortlistOption && isSchedulingAgentActive ? (
              <CalenderSparkle
                className="h-4 w-4 shrink-0"
                color="hsl(var(--color-primary-900))"
              />
            ) : null}
          </Row>
          {isShortlistGate ? (
            <Row className="items-center gap-1">
              <TriangleAlertIcon className="h-3 w-3 shrink-0 text-amber-500" />
              <Text className="text-xs font-medium text-amber-600">
                Set up your calendar to enable shortlisting
              </Text>
            </Row>
          ) : null}
        </Column>
      </DropdownMenuItem>
    );
  };

  const render = () => {
    return (
      <>
        <DropdownMenu onOpenChange={setOpen} open={open}>
          <DropdownMenuTrigger asChild>
            <Button
              className="relative h-8 gap-1.5 rounded-full px-3 text-xs font-medium text-slate-500 hover:text-slate-500"
              size="sm"
              variant="outlineSecondary"
            >
              <div className={cn("h-2.5 w-2.5 rounded-full", statusDotColor)} />
              {statusLabel}
              <ChevronDownIcon className="h-3.5 w-3.5" />
              {candidacyInfo.lastStatusChangeByAgent && (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-purple-100 text-purple-600 ring-1 ring-white">
                  <SparklesIcon className="!h-2.5 !w-2.5" />
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[250px]">
            {filteredCandidacyStatusOptions.map(renderStatusOption)}
          </DropdownMenuContent>
        </DropdownMenu>
        {takeover && (
          <ShortlistTakeoverDialog
            {...takeover}
            onClose={() => {
              setTakeover(null);
            }}
          />
        )}
      </>
    );
  };

  return render();
};
